import React, { useState } from 'react'
import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import ReportTable from '../../components/reports/table'
import { ReportControls, BalancePeriodSelect, DisplayToggle, ReportPeriodRange } from './report-controls'
import { ILeaveBalanceReportProps } from './types'
import { leaveBalanceReportColumns, ReportPeriod } from '@vacationtracker/shared/types/export'
import IntlMessages from '../../util/IntlMessages'

const LeaveBalanceReport: React.FC<ILeaveBalanceReportProps> = ({
  companyData,
  defaultDates,
  calculationStartYear,
  data,
  total,
  page,
  loading,
  display,
  defaultFilters,
  onFiltersChange,
  onPeriodChange,
  onDisplayChange,
  loadNext,
  loadPrevious,
  download,
}) => {
  const [balancePeriod, setBalancePeriod] = useState<ReportPeriod>('CURRENT_YEAR')
  const [hideEmptyColumns, setHideEmptyColumns] = useState<boolean>(() => {
    const stored = localStorage.getItem('leave-balance-report_hideEmptyColumns')
    if (stored !== null) return stored === 'true'
    localStorage.setItem('leave-balance-report_hideEmptyColumns', 'true')
    return true
  })

  const handleHideEmptyColumnsChange = (value: boolean) => {
    setHideEmptyColumns(value)
    localStorage.setItem('leave-balance-report_hideEmptyColumns', String(value))
  }

  const onBalancePeriodChange = (period: ReportPeriod) => {
    setBalancePeriod(period)
    if (period === 'YEAR_TO_DATE') {
      onPeriodChange([dayjs().startOf('year'), dayjs()], period)
    } else {
      onPeriodChange(defaultDates, period)
    }
  }

  const show = (
    <>
      <BalancePeriodSelect value={balancePeriod} onChange={onBalancePeriodChange} />
      {balancePeriod === 'YEAR_TO_DATE' && (
        <>
          <span>
            <IntlMessages id="export.yearToDateAsOf" />
          </span>
          <DatePicker
            defaultValue={dayjs()}
            format="YYYY-MM-DD"
            allowClear={false}
            disabledDate={(current) => {
              const maxDate = dayjs().add(1, 'year').endOf('year')
              if (current.isAfter(maxDate, 'day')) return true
              if (calculationStartYear) {
                const minDate = dayjs(calculationStartYear, 'YYYY').startOf('year')
                if (current.isBefore(minDate, 'day')) return true
              }
              return false
            }}
            onChange={(date: Dayjs | null) => {
              if (date) {
                onPeriodChange([dayjs().startOf('year'), date], 'YEAR_TO_DATE')
              }
            }}
          />
        </>
      )}
      {balancePeriod === 'CUSTOM_DAYS' && <ReportPeriodRange defaultDates={defaultDates} format="YYYY-MM-DD" onChange={onPeriodChange} />}
      <DisplayToggle value={display} onChange={onDisplayChange} />
    </>
  )

  return (
    <div>
      <ReportControls show={show} companyData={companyData} defaultFilters={defaultFilters} onFiltersChange={onFiltersChange} />
      <ReportTable
        columnNames={leaveBalanceReportColumns}
        data={data}
        total={total}
        loadNext={loadNext}
        loadPrevious={loadPrevious}
        download={download}
        loading={loading}
        dateFormat={'YYYY-MM-DD'}
        display={display}
        page={page}
        reportType="leave-balance-report"
        editableColumns={true}
        hideEmptyColumns={hideEmptyColumns}
        onHideEmptyColumnsChange={handleHideEmptyColumnsChange}
      />
    </div>
  )
}

export default LeaveBalanceReport
